import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  useDisclosure,
  useToast,
  Badge,
  Tag,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Image,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'

// Custom components
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { QRCodeCanvas } from 'qrcode.react'
import { MdQrCode, MdContentCopy, MdConfirmationNumber } from 'react-icons/md'

// Import event-specific modals and hooks
import { useGetEvents, useDeleteEvent } from 'queries/event.query'
import { CustomPagination } from 'views/admin/communities/components/pagination'
import { DeleteEventModal } from './DeleteEventModal'
import TicketPreviewModal from './TicketPreviewModal'

const columnHelper = createColumnHelper()

export default function EventTable() {
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const navigate = useNavigate()
  const toast = useToast()

  const {
    isOpen: isDeleteOpen,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure()

  const {
    isOpen: isPreviewOpen,
    onOpen: onOpenPreview,
    onClose: onClosePreview,
  } = useDisclosure()

  const [selectedForDeletion, setSelectedForDeletion] = React.useState(null)
  const [previewEvent, setPreviewEvent] = React.useState(null)

  const handlePreviewOpen = (event) => {
    setPreviewEvent(event)
    onOpenPreview()
  }

  const { mutate: deleteMutate, isPending } = useDeleteEvent()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  })

  const { data, isLoading, isError, refetch } = useGetEvents(
    pagination.current,
    pagination.pageSize,
  )

  const rows = React.useMemo(() => {
    const now = Date.now()
    const getTimestamp = value => {
      if (!value) return Number.POSITIVE_INFINITY
      const timestamp = new Date(value).getTime()
      return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp
    }

    return [...(data?.data || [])].sort((eventA, eventB) => {
      const startA = getTimestamp(eventA.start_datetime)
      const startB = getTimestamp(eventB.start_datetime)
      const endA = getTimestamp(eventA.end_datetime || eventA.start_datetime)
      const endB = getTimestamp(eventB.end_datetime || eventB.start_datetime)
      const pastA = endA < now
      const pastB = endB < now

      if (pastA !== pastB) return pastA ? 1 : -1
      return pastA ? endB - endA : startA - startB
    })
  }, [data?.data])

  const handleEditOpen = event => {
    navigate(`/admin/events/${event.id}/edit`)
  }

  const handleSingleDelete = id => {
    setSelectedForDeletion([id])
    onOpenDelete()
  }

  const onDelete = () => {
    if (selectedForDeletion && selectedForDeletion.length > 0) {
      deleteMutate(selectedForDeletion[0], {
        onSuccess: () => {
          onCloseDelete()
          setSelectedForDeletion(null)
          refetch()
          toast({
            title: 'Event Deleted.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
        onError: err => {
          toast({
            title: 'Failed to Delete Event.',
            description: err.response?.data?.message || 'There was an error deleting the event.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        },
      })
    }
  }

  const columns = [
    columnHelper.accessor('title', {
      id: 'title',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }}>
          Title
        </Text>
      ),
      cell: info => (
        <Text color={textColor} fontSize='sm' fontWeight='700'>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('start_datetime', {
      id: 'start_datetime',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }}>
          Date
        </Text>
      ),
      cell: ({ row }) => {
        const val = row.original.start_datetime;
        if (!val) return '-';
        const start = new Date(val);
        const end = new Date(row.original.end_datetime || val);
        const now = new Date();
        const isOngoing = start <= now && end >= now;
        const isPast = end < now;
        const relativeDate = isPast ? end : start;
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const targetDay = new Date(relativeDate.getFullYear(), relativeDate.getMonth(), relativeDate.getDate());
        const dayDifference = Math.round((targetDay.getTime() - today.getTime()) / 86400000);
        const days = Math.abs(dayDifference);
        const sameDay = days === 0;
        const label = isOngoing
          ? 'Ongoing'
          : isPast
            ? `Past${sameDay ? ' · today' : ` · ${days}d ago`}`
            : sameDay
              ? 'Today'
              : `Upcoming · in ${days}d`;
        const colorScheme = isOngoing ? 'green' : isPast ? 'gray' : 'blue';
        const match = String(val).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
        const formatted = match
          ? `${match[3]}/${match[2]}/${match[1]}, ${match[4] || '00'}:${match[5] || '00'}`
          : start.toLocaleString();
        return (
          <Flex align='center' justify='center' wrap='wrap' gap={2}>
            <Text color={textColor} fontSize='sm' whiteSpace='nowrap'>
              {formatted}
            </Text>
            <Badge colorScheme={colorScheme} fontSize='10px' whiteSpace='nowrap'>
              {label}
            </Badge>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('cover_image', {
      id: 'cover_image',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          Cover Image
        </Text>
      ),
      cell: info => {
        const path = info.getValue()
        const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
        
        let imageUrl = null;
        if (path) {
          if (path.startsWith('http')) {
            imageUrl = path;
          } else {
            // Remove leading slash if present, then ensure we don't duplicate /storage/
            const cleanPath = path.startsWith('/') ? path.substring(1) : path;
            const finalPath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
            imageUrl = `${baseUrl}/${finalPath}`;
          }
        }
        
        return (
          <Flex justifyContent='center' alignItems='center'>
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt='Cover Image'
                w='50px'
                h='50px'
                borderRadius='md'
                objectFit='cover'
              />
            ) : (
              <Text color='gray.400' fontSize='xs'>
                No Image
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          Category
        </Text>
      ),
      cell: info => {
        const category = info.getValue()
        let colorScheme
        switch (category) {
          case 'concert': colorScheme = 'purple'; break;
          case 'cultural': colorScheme = 'orange'; break;
          case 'sports': colorScheme = 'green'; break;
          default: colorScheme = 'gray';
        }
        return (
          <Flex justifyContent='center' alignItems='center'>
            <Tag size='sm' colorScheme={colorScheme}>
              {category || 'N/A'}
            </Tag>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          Status
        </Text>
      ),
      cell: ({ row }) => {
        const status = row.original?.status
        const isSoldOut = Boolean(row.original?.is_sold_out)
        let colorScheme
        switch (status) {
          case 'PUBLISHED': colorScheme = 'green'; break;
          case 'DRAFT': colorScheme = 'gray'; break;
          case 'CANCELLED': colorScheme = 'red'; break;
          case 'POSTPONED': colorScheme = 'orange'; break;
          case 'COMPLETED': colorScheme = 'blue'; break;
          default: colorScheme = 'gray';
        }
        return (
          <Flex justifyContent='center' alignItems='center' direction='column' gap={1}>
            <Badge colorScheme={colorScheme}>
              {status || 'N/A'}
            </Badge>
            {isSoldOut && (
              <Badge colorScheme='red' variant='solid' fontSize='9px' px={2} py={0.5} borderRadius='full'>
                Sold Out
              </Badge>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('share', {
      id: 'share',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          SHARE
        </Text>
      ),
      cell: ({ row }) => {
        const event = row.original;
        const link = `https://bamyanentertainment.de/events/${event.slug || event.id}`;
        
        const copyLink = () => {
          navigator.clipboard.writeText(link);
          toast({
            title: 'Link Copied',
            status: 'success',
            duration: 2000,
          });
        };

        const downloadQR = () => {
          const canvas = document.getElementById(`qr-${event.id}`);
          if (canvas) {
            const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
            let downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `event-${event.slug || event.id}-qr.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
          }
        };

        return (
          <Flex justifyContent='center' alignItems='center' gap={2}>
            <Box display="none">
              <QRCodeCanvas
                id={`qr-${event.id}`}
                value={link}
                size={500}
                level={"H"}
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin={true}
                imageSettings={{
                  src: "/logo-512x512.png",
                  height: 100,
                  width: 100,
                  excavate: true,
                }}
              />
            </Box>
            <Button size="sm" colorScheme="blue" variant="outline" onClick={copyLink} title="Copy Link">
              <MdContentCopy />
            </Button>
            <Button size="sm" colorScheme="teal" variant="outline" onClick={downloadQR} title="Download QR">
              <MdQrCode />
            </Button>
            <Button
              size="sm"
              colorScheme="purple"
              variant="outline"
              onClick={() => handlePreviewOpen(event)}
              title="Preview Ticket"
            >
              <MdConfirmationNumber />
            </Button>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          ACTIONS
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' isAttached>
            <Button colorScheme='brand' onClick={() => handleEditOpen(row.original)}>
              <EditIcon />
            </Button>
            <Button colorScheme='red' onClick={() => handleSingleDelete(row.original.id)}>
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load event data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Events
        </Text>
        <Button
          leftIcon={<AiOutlinePlus />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={() => navigate('/admin/events/create')}
        >
          New Event
        </Button>
      </Flex>
      <Box
        maxH='600px'
        overflowY='auto'
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
      >
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    textAlign='center'
                  >
                    <Flex
                      justifyContent='center'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {data && (
        <CustomPagination
          total={data?.total || rows.length}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onChange={(page, pageSize) =>
            setPagination({ current: page, pageSize: pageSize })
          }
        />
      )}
      <DeleteEventModal
        isOpen={isDeleteOpen}
        onClose={onCloseDelete}
        onDelete={onDelete}
        isLoading={isPending}
        isDisabled={isPending}
      />
      <TicketPreviewModal
        isOpen={isPreviewOpen}
        onClose={onClosePreview}
        event={previewEvent}
      />
    </Card>
  )
}
